/**
 * The page "Cost and runs": TanStack Query hooks over the five FinOps routes (14 Oct 2026).
 *
 * - `GET /metrics/cost` (`getCostReport`): the recorded model cost, the caps, the trend,
 *   the forecast and the Azure view. Admins only (capability `see_finops`, 15 Oct 2026).
 * - `GET /metrics/cost/summary` (`getCostSummary`): the month, no case and no entity.
 *   Admins only; no page reads it since 15 Oct 2026.
 * - `GET /metrics/runs?hours=24|168` (`getRunsReport`): the mail import runs, the re-read
 *   of stored mail, the data jobs, the queue and the errors per step.
 * - `GET /metrics/model-health?hours=24|168` (`getModelHealth`): calls, errors, "Too many
 *   requests" (HTTP 429) and the time per model and step.
 * - `GET /metrics/alerts?days=1..30` (`getFinopsAlerts`): the caps and alert levels that
 *   were reached, and the Azure alerts when the reader is connected.
 *
 * The first part of each query key is the request key of the usage vocabulary
 * (`USAGE_REQUEST_KEYS` in `lib/usage-events.ts`), so a failed read is recorded as
 * `what: "metrics-cost"` and so on. The server answers from a cache (5 minutes; model
 * health 60 seconds), so the page asks no more often than that. The server is the gate;
 * `enabled` only saves a request that would be refused.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  type CostReport,
  type CostSummary,
  type FinopsAlertsReport,
  getCostReport,
  getCostSummary,
  getFinopsAlerts,
  getModelHealth,
  getRunsReport,
  type ModelHealthReport,
  type RunsReport,
} from "./generated";

export type {
  AzureAlertRow,
  AzureCost,
  CostCaseRow,
  CostDay,
  CostForecast,
  CostPerCase,
  CostPeriod,
  CostReport,
  CostSlice,
  CostSummary,
  FinopsAlertsReport,
  FinopsEventRow,
  GuardState,
  IngestRunRow,
  JobRow,
  ModelHealthReport,
  ModelHealthRow,
  PriceRow,
  RereadProgress,
  RunsReport,
  SlowCase,
  StepErrors,
} from "./generated";

/** The two windows of the tabs "Runs" and "Model health": 24 hours or 7 days. */
export type FinopsHours = 24 | 168;

/** The days of the tab "Alerts" (the server takes 1 to 30). */
export const FINOPS_ALERT_DAYS = 7;

// One named key each, so `tests/usage-capture.test.mjs` finds the first part of every key.
const costKey = ["metrics-cost"] as const;
const summaryKey = ["metrics-cost-summary"] as const;
const runsKey = (hours: FinopsHours) => ["metrics-runs", hours] as const;
const healthKey = (hours: FinopsHours) => ["metrics-model-health", hours] as const;
const alertsKey = (days: number) => ["metrics-alerts", days] as const;

/** The query keys; the first part of each is its request key of the usage vocabulary. */
export const finopsKeys = {
  cost: costKey,
  summary: summaryKey,
  runs: runsKey,
  health: healthKey,
  alerts: alertsKey,
};

/** The server keeps the cost, runs and alerts 5 minutes, model health 60 seconds. */
const REPORT_STALE_MS = 5 * 60_000;
const HEALTH_STALE_MS = 60_000;

/** The cost report (admins). */
export function useCostReport(enabled: boolean) {
  return useQuery({
    queryKey: finopsKeys.cost,
    queryFn: async (): Promise<CostReport | null> => {
      const { data } = await getCostReport({ throwOnError: true });
      return data ?? null;
    },
    enabled,
    retry: false,
    staleTime: REPORT_STALE_MS,
  });
}

/** The executive view: the month, the forecast, the budget and the paused caps. */
export function useCostSummary(enabled: boolean) {
  return useQuery({
    queryKey: finopsKeys.summary,
    queryFn: async (): Promise<CostSummary | null> => {
      const { data } = await getCostSummary({ throwOnError: true });
      return data ?? null;
    },
    enabled,
    retry: false,
    staleTime: REPORT_STALE_MS,
  });
}

/** The runs of the mail import, the re-read, the data jobs and the errors per step. */
export function useRunsReport(hours: FinopsHours, enabled: boolean) {
  return useQuery({
    queryKey: finopsKeys.runs(hours),
    queryFn: async (): Promise<RunsReport | null> => {
      const { data } = await getRunsReport({ query: { hours }, throwOnError: true });
      return data ?? null;
    },
    enabled,
    retry: false,
    staleTime: REPORT_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

/** Calls, errors, throttles and the time per model and step. */
export function useModelHealth(hours: FinopsHours, enabled: boolean) {
  return useQuery({
    queryKey: finopsKeys.health(hours),
    queryFn: async (): Promise<ModelHealthReport | null> => {
      const { data } = await getModelHealth({ query: { hours }, throwOnError: true });
      return data ?? null;
    },
    enabled,
    retry: false,
    staleTime: HEALTH_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

/** The caps and alert levels that were reached, and the Azure alerts. */
export function useFinopsAlerts(days: number, enabled: boolean) {
  return useQuery({
    queryKey: finopsKeys.alerts(days),
    queryFn: async (): Promise<FinopsAlertsReport | null> => {
      const { data } = await getFinopsAlerts({ query: { days }, throwOnError: true });
      return data ?? null;
    },
    enabled,
    retry: false,
    staleTime: REPORT_STALE_MS,
  });
}
